import {
    MakeLogicType,
    LogicWrapper,
    actions,
    connect,
    events,
    kea,
    key,
    listeners,
    path,
    props,
    reducers,
    selectors,
} from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'
import posthog from 'posthog-js'

import { LemonDialog, LemonInput } from '@posthog/lemon-ui'

import { ApiError } from 'lib/api'
import { tryShowMCPHint } from 'lib/components/MCPHint/mcpHintLogic'
import { SetupTaskId, globalSetupLogic } from 'lib/components/ProductSetup'
import { FEATURE_FLAGS } from 'lib/constants'
import { LemonField } from 'lib/lemon-ui/LemonField'
import { lemonToast } from 'lib/lemon-ui/LemonToast/LemonToast'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { accessLevelSatisfied } from 'lib/utils/accessControlUtils'
import { deleteInsightWithUndo } from 'lib/utils/deleteWithUndo'
import { InsightEventSource, eventUsageLogic } from 'lib/utils/eventUsageLogic'
import { isEmptyObject, isObject } from 'lib/utils/guards'
import { objectsEqual } from 'lib/utils/objects'
import { isDashboardFilterEmpty } from 'scenes/dashboard/dashboardFilterEmpty'
import { DashboardLoadAction, dashboardLogic } from 'scenes/dashboard/dashboardLogic'
import { insightSceneLogic } from 'scenes/insights/insightSceneLogic'
import { keyForInsightLogicProps } from 'scenes/insights/sharedUtils'
import { summarizeInsight } from 'scenes/insights/summarizeInsight'
import { savedInsightsLogic } from 'scenes/saved-insights/savedInsightsLogic'
import { sceneLogic } from 'scenes/sceneLogic'
import { Scene } from 'scenes/sceneTypes'
import { mathsLogic } from 'scenes/trends/mathsLogic'
import { IndexedTrendResult } from 'scenes/trends/types'
import { urls } from 'scenes/urls'
import { userLogic } from 'scenes/userLogic'

import { getLastNewFolder, refreshTreeItem } from '~/layout/panel-layout/ProjectTree/projectTreeLogic'
import { cohortsModel } from '~/models/cohortsModel'
import { dashboardsModel } from '~/models/dashboardsModel'
import { groupsModel } from '~/models/groupsModel'
import { insightsModel } from '~/models/insightsModel'
import { tagsModel } from '~/models/tagsModel'
import { DashboardFilter, HogQLVariable, Node, TileFilters } from '~/queries/schema/schema-general'
import {
    convertDataTableNodeToDataVisualizationNode,
    isFunnelsQuery,
    isLifecycleQuery,
    isNodeWithSource,
    isPathsQuery,
    isRetentionQuery,
    isStickinessQuery,
    isTrendsQuery,
    isValidQueryForExperiment,
} from '~/queries/utils'
import {
    AccessControlLevel,
    AccessControlResourceType,
    DashboardTile,
    DashboardType,
    InsightLogicProps,
    InsightShortId,
    ItemMode,
    QueryBasedInsightModel,
    SetInsightOptions,
} from '~/types'

import { insightAlertsLogic } from 'products/alerts/frontend/logic/insightAlertsLogic'

import type { AlertType } from '../../../../products/alerts/frontend/types'
import type { InsightFilterOverrideContextApi } from '../../../../products/product_analytics/frontend/generated/api.schemas'
import type { FeatureFlagsSet } from '../../lib/logic/featureFlagLogic'
import type { ProductIntentProperties } from '../../lib/utils/product-intents'
import type { Noun } from '../../models/groupsModel'
import type { QueryStatus } from '../../queries/schema/schema-general'
import type { CohortType, DashboardTileBasicType, TeamPublicType, TeamType, UserBasicType, UserType } from '../../types'
import { teamLogic } from '../teamLogic'
import type { MathDefinition } from '../trends/mathsLogic'
import { insightDataLogic, isInsightSceneInstance } from './insightDataLogic'
import { getInsightId } from './utils'
import { insightsApi } from './utils/api'

export const UNSAVED_INSIGHT_MIN_REFRESH_INTERVAL_MINUTES = 3

export const createEmptyInsight = (
    shortId: InsightShortId | `new-${string}` | 'new'
): Partial<QueryBasedInsightModel> => ({
    short_id: shortId !== 'new' && !shortId.startsWith('new-') ? (shortId as InsightShortId) : undefined,
    name: undefined,
    description: '',
    tags: [],
    dashboards: [],
    result: null,
})

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface insightLogicValues {
    cohortsById: Partial<Record<number | string, CohortType>> // cohortsModel
    featureFlags: FeatureFlagsSet // featureFlagLogic
    aggregationLabel: (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun // groupsModel
    mathDefinitions: Partial<Record<string, MathDefinition>> // mathsLogic
    activeSceneId: string | null // sceneLogic
    currentTeam: TeamPublicType | TeamType | null // teamLogic
    currentTeamId: number | null // teamLogic
    user: UserType | null // userLogic
    accessDeniedToInsight: boolean
    canEditInsight: boolean
    derivedName: string
    editingDisabledReason: 'Discard overrides to edit the insight.' | null
    hasDashboardItemId: boolean
    hasOverrides: boolean
    highlightedSeries: IndexedTrendResult | null
    insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
    insightChanged: boolean
    insightFeedback: 'disliked' | 'liked' | null
    insightId: number | null
    insightLoading: boolean
    insightName: string
    insightProps: InsightLogicProps
    insightSaving: boolean
    isInDashboardContext: boolean
    isInExperimentContext: boolean
    isInViewMode: boolean
    isSavingTags: boolean
    isUsingPathsV1: boolean
    isUsingPathsV2: boolean | string | undefined
    previousQuery: Node | null
    query: Node | null
    savedInsight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
    showPersonsModal: boolean
    suggestedQuery: Node | null
    supportsCreatingExperiment: boolean | null
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface insightLogicActions {
    loadTags: () => any // tagsModel
    addProductIntent: (properties: ProductIntentProperties) => ProductIntentProperties // teamLogic
    confirmDeleteInsight: (dashboardId: number | null) => {
        dashboardId: number | null
    }
    deleteInsight: (dashboardId: number | null) => {
        dashboardId: number | null
    }
    duplicateInsight: (
        insight: QueryBasedInsightModel,
        redirectToInsight?: any
    ) => {
        insight: QueryBasedInsightModel<Node<Record<string, any>>>
        redirectToInsight: any
    }
    handleInsightSuggested: (suggestedInsight: Node | null) => {
        suggestedInsight: Node<Record<string, any>> | null
    }
    highlightSeries: (series: IndexedTrendResult | null) => {
        series: IndexedTrendResult | null
    }
    loadInsight: (
        shortId: InsightShortId,
        filtersOverride?: DashboardFilter | null,
        variablesOverride?: Record<string, HogQLVariable> | null,
        tileFiltersOverride?: DashboardFilter | null
    ) => {
        filtersOverride: DashboardFilter | null | undefined
        shortId: InsightShortId
        tileFiltersOverride: DashboardFilter | null | undefined
        variablesOverride: Record<string, HogQLVariable> | null | undefined
    }
    loadInsightFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadInsightSuccess: (
        insight: {
            _create_in_folder?: string | null | undefined
            alerts?: AlertType[] | undefined
            cache_target_age?: string | null | undefined
            created_at: string
            created_by: UserBasicType | null
            dashboard_tiles: DashboardTileBasicType[] | null
            dashboards: number[] | null
            deleted: boolean
            derived_name?: string | null | undefined
            description?: string | undefined
            disable_baseline?: boolean | undefined
            favorited?: boolean | undefined
            filter_override_context?: InsightFilterOverrideContextApi | null | undefined
            id: number
            is_cached?: boolean | undefined
            is_sample: boolean
            last_modified_at: string
            last_modified_by: UserBasicType | null
            last_refresh: string | null
            last_viewed_at?: string | null | undefined
            name: string
            next?: string | undefined
            next_allowed_client_refresh?: string | null | undefined
            order: number | null
            query: Node<Record<string, any>> | null
            query_status?: QueryStatus | undefined
            result: any
            saved: boolean
            short_id: InsightShortId
            tags?: string[] | undefined
            timezone?: string | null | undefined
            updated_at: string
            user_access_level: AccessControlLevel
            view_count?: number | undefined
            viewers?: UserBasicType[] | undefined
        },
        payload?: {
            filtersOverride: DashboardFilter | null | undefined
            shortId: InsightShortId
            tileFiltersOverride: DashboardFilter | null | undefined
            variablesOverride: Record<string, HogQLVariable> | null | undefined
        }
    ) => {
        insight: {
            _create_in_folder?: string | null | undefined
            alerts?: AlertType[] | undefined
            cache_target_age?: string | null | undefined
            created_at: string
            created_by: UserBasicType | null
            dashboard_tiles: DashboardTileBasicType[] | null
            dashboards: number[] | null
            deleted: boolean
            derived_name?: string | null | undefined
            description?: string | undefined
            disable_baseline?: boolean | undefined
            favorited?: boolean | undefined
            filter_override_context?: InsightFilterOverrideContextApi | null | undefined
            id: number
            is_cached?: boolean | undefined
            is_sample: boolean
            last_modified_at: string
            last_modified_by: UserBasicType | null
            last_refresh: string | null
            last_viewed_at?: string | null | undefined
            name: string
            next?: string | undefined
            next_allowed_client_refresh?: string | null | undefined
            order: number | null
            query: Node<Record<string, any>> | null
            query_status?: QueryStatus | undefined
            result: any
            saved: boolean
            short_id: InsightShortId
            tags?: string[] | undefined
            timezone?: string | null | undefined
            updated_at: string
            user_access_level: AccessControlLevel
            view_count?: number | undefined
            viewers?: UserBasicType[] | undefined
        }
        payload?: {
            filtersOverride: DashboardFilter | null | undefined
            shortId: InsightShortId
            tileFiltersOverride: DashboardFilter | null | undefined
            variablesOverride: Record<string, HogQLVariable> | null | undefined
        }
    }
    onReapplySuggestedInsight: () => {
        value: true
    }
    onRejectSuggestedInsight: () => {
        value: true
    }
    reloadSavedInsights: () => {
        value: true
    }
    saveAs: (
        redirectToViewMode?: boolean,
        persist?: boolean,
        folder?: string | null
    ) => {
        folder: string | null | undefined
        persist: boolean | undefined
        redirectToViewMode: boolean | undefined
    }
    saveAsConfirmation: (
        name: string,
        redirectToViewMode?: any,
        persist?: any,
        folder?: string | null
    ) => {
        folder: string | null | undefined
        name: string
        persist: any
        redirectToViewMode: any
    }
    saveInsight: (
        redirectToViewMode?: boolean,
        folder?: string | null
    ) => {
        folder: string | null
        redirectToViewMode: boolean
    }
    saveInsightFailure: () => {
        value: true
    }
    saveInsightSuccess: () => {
        value: true
    }
    setAccessDeniedToInsight: () => {
        value: true
    }
    setInsight: (
        insight: Partial<QueryBasedInsightModel>,
        options: SetInsightOptions
    ) => {
        insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
        options: SetInsightOptions
    }
    setInsightFeedback: (feedback: 'disliked' | 'liked') => {
        feedback: 'disliked' | 'liked'
    }
    setInsightMetadata: (
        metadataUpdate: Partial<Pick<QueryBasedInsightModel, 'description' | 'favorited' | 'name' | 'tags'>>
    ) => {
        metadataUpdate: Partial<
            Pick<QueryBasedInsightModel<Node<Record<string, any>>>, 'description' | 'favorited' | 'name' | 'tags'>
        >
    }
    setInsightMetadataFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    setInsightMetadataLocal: (
        metadataUpdate: Partial<Pick<QueryBasedInsightModel, 'description' | 'favorited' | 'name' | 'tags'>>
    ) => {
        metadataUpdate: Partial<
            Pick<QueryBasedInsightModel<Node<Record<string, any>>>, 'description' | 'favorited' | 'name' | 'tags'>
        >
    }
    setInsightMetadataSuccess: (
        insight: {
            _create_in_folder?: string | null | undefined
            alerts?: AlertType[] | undefined
            cache_target_age?: string | null | undefined
            created_at?: string | undefined
            created_by?: UserBasicType | null | undefined
            dashboard_tiles?: DashboardTileBasicType[] | null | undefined
            dashboards?: number[] | null | undefined
            deleted?: boolean | undefined
            derived_name?: string | null | undefined
            description?: string | undefined
            disable_baseline?: boolean | undefined
            favorited?: boolean | undefined
            filter_override_context?: InsightFilterOverrideContextApi | null | undefined
            id?: number | undefined
            is_cached?: boolean | undefined
            is_sample?: boolean | undefined
            last_modified_at?: string | undefined
            last_modified_by?: UserBasicType | null | undefined
            last_refresh?: string | null | undefined
            last_viewed_at?: string | null | undefined
            name?: string | undefined
            next?: string | undefined
            next_allowed_client_refresh?: string | null | undefined
            order?: number | null | undefined
            query?: Node<Record<string, any>> | null | undefined
            query_status?: QueryStatus | undefined
            result?: any
            saved?: boolean | undefined
            short_id?: InsightShortId | undefined
            tags?: string[] | undefined
            timezone?: string | null | undefined
            updated_at?: string | undefined
            user_access_level?: AccessControlLevel | undefined
            view_count?: number | undefined
            viewers?: UserBasicType[] | undefined
        },
        payload?: {
            metadataUpdate: Partial<
                Pick<QueryBasedInsightModel<Node<Record<string, any>>>, 'description' | 'favorited' | 'name' | 'tags'>
            >
        }
    ) => {
        insight: {
            _create_in_folder?: string | null | undefined
            alerts?: AlertType[] | undefined
            cache_target_age?: string | null | undefined
            created_at?: string | undefined
            created_by?: UserBasicType | null | undefined
            dashboard_tiles?: DashboardTileBasicType[] | null | undefined
            dashboards?: number[] | null | undefined
            deleted?: boolean | undefined
            derived_name?: string | null | undefined
            description?: string | undefined
            disable_baseline?: boolean | undefined
            favorited?: boolean | undefined
            filter_override_context?: InsightFilterOverrideContextApi | null | undefined
            id?: number | undefined
            is_cached?: boolean | undefined
            is_sample?: boolean | undefined
            last_modified_at?: string | undefined
            last_modified_by?: UserBasicType | null | undefined
            last_refresh?: string | null | undefined
            last_viewed_at?: string | null | undefined
            name?: string | undefined
            next?: string | undefined
            next_allowed_client_refresh?: string | null | undefined
            order?: number | null | undefined
            query?: Node<Record<string, any>> | null | undefined
            query_status?: QueryStatus | undefined
            result?: any
            saved?: boolean | undefined
            short_id?: InsightShortId | undefined
            tags?: string[] | undefined
            timezone?: string | null | undefined
            updated_at?: string | undefined
            user_access_level?: AccessControlLevel | undefined
            view_count?: number | undefined
            viewers?: UserBasicType[] | undefined
        }
        payload?: {
            metadataUpdate: Partial<
                Pick<QueryBasedInsightModel<Node<Record<string, any>>>, 'description' | 'favorited' | 'name' | 'tags'>
            >
        }
    }
    setPreviousQuery: (previousQuery: Node | null) => {
        previousQuery: Node<Record<string, any>> | null
    }
    setSuggestedQuery: (suggestedQuery: Node | null) => {
        suggestedQuery: Node<Record<string, any>> | null
    }
    updateInsight: (
        insightUpdate: Partial<QueryBasedInsightModel>,
        callback?: () => void
    ) => {
        callback: (() => void) | undefined
        insightUpdate: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
    }
    updateInsightFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateInsightSuccess: (
        insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>,
        payload?: {
            callback: (() => void) | undefined
            insightUpdate: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
        }
    ) => {
        insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
        payload?: {
            callback: (() => void) | undefined
            insightUpdate: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface insightLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        insightProps: (arg: any) => InsightLogicProps
        query: (arg: Node<Record<string, any>> | null) => Node | null
        isInDashboardContext: (arg: any) => boolean
        hasDashboardItemId: (arg: any) => boolean
        isInExperimentContext: (location: { hash: string; pathname: string; search: string }) => boolean
        isInViewMode: (location: { hash: string; pathname: string; search: string }) => boolean
        derivedName: (
            query: Node<Record<string, any>> | null,
            aggregationLabel: (groupTypeIndex: number | null | undefined, deferToUserWording?: boolean) => Noun, // groupsModel
            cohortsById: Partial<Record<number | string, CohortType>>,
            mathDefinitions: Partial<Record<string, MathDefinition>>
        ) => string
        insightName: (
            insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>,
            derivedName: string
        ) => string
        insightId: (insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>) => number | null
        canEditInsight: (insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>) => boolean
        insightChanged: (
            insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>,
            savedInsight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
        ) => boolean
        showPersonsModal: (
            query: Node<Record<string, any>> | null,
            insightProps: InsightLogicProps<import('~/queries/schema/schema-general').QuerySchema>,
            featureFlags: FeatureFlagsSet
        ) => boolean
        supportsCreatingExperiment: (
            insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>,
            activeSceneId: string | null
        ) => boolean | null
        isUsingPathsV1: (featureFlags: FeatureFlagsSet) => boolean
        isUsingPathsV2: (featureFlags: FeatureFlagsSet) => boolean | string | undefined
        hasOverrides: (arg: any, arg2: any, arg3: any) => boolean
        editingDisabledReason: (hasOverrides: boolean) => 'Discard overrides to edit the insight.' | null
    }
}

export type insightLogicType = MakeLogicType<
    insightLogicValues,
    insightLogicActions,
    InsightLogicProps,
    insightLogicMeta
>

export const insightLogic: LogicWrapper<insightLogicType> = kea<insightLogicType>([
    props({ filtersOverride: null, variablesOverride: null, tileFiltersOverride: null } as InsightLogicProps),
    key((props) => keyForInsightLogicProps('new')(props)),
    path((key) => ['scenes', 'insights', 'insightLogic', key]),
    connect(() => ({
        values: [
            teamLogic,
            ['currentTeamId', 'currentTeam'],
            groupsModel,
            ['aggregationLabel'],
            cohortsModel,
            ['cohortsById'],
            mathsLogic,
            ['mathDefinitions'],
            userLogic,
            ['user'],
            featureFlagLogic,
            ['featureFlags'],
            sceneLogic,
            ['activeSceneId'],
        ],
        actions: [tagsModel, ['loadTags'], teamLogic, ['addProductIntent']],
        logic: [eventUsageLogic, dashboardsModel],
    })),

    actions({
        setInsight: (insight: Partial<QueryBasedInsightModel>, options: SetInsightOptions) => ({
            insight,
            options,
        }),
        saveAs: (redirectToViewMode?: boolean, persist?: boolean, folder?: string | null) => ({
            redirectToViewMode,
            persist,
            folder,
        }),
        saveAsConfirmation: (name: string, redirectToViewMode = false, persist = true, folder?: string | null) => ({
            name,
            redirectToViewMode,
            persist,
            folder,
        }),
        saveInsight: (redirectToViewMode: boolean = true, folder: string | null = null) => ({
            redirectToViewMode,
            folder,
        }),
        saveInsightSuccess: true,
        saveInsightFailure: true,
        loadInsight: (
            shortId: InsightShortId,
            filtersOverride?: DashboardFilter | null,
            variablesOverride?: Record<string, HogQLVariable> | null,
            tileFiltersOverride?: DashboardFilter | null
        ) => ({
            shortId,
            filtersOverride,
            variablesOverride,
            tileFiltersOverride,
        }),
        updateInsight: (insightUpdate: Partial<QueryBasedInsightModel>, callback?: () => void) => ({
            insightUpdate,
            callback,
        }),
        setInsightMetadata: (
            metadataUpdate: Partial<Pick<QueryBasedInsightModel, 'name' | 'description' | 'tags' | 'favorited'>>
        ) => ({
            metadataUpdate,
        }),
        setInsightMetadataLocal: (
            metadataUpdate: Partial<Pick<QueryBasedInsightModel, 'name' | 'description' | 'tags' | 'favorited'>>
        ) => ({
            metadataUpdate,
        }),
        highlightSeries: (series: IndexedTrendResult | null) => ({ series }),
        setAccessDeniedToInsight: true,
        handleInsightSuggested: (suggestedInsight: Node | null) => ({ suggestedInsight }),
        onRejectSuggestedInsight: true,
        onReapplySuggestedInsight: true,
        setPreviousQuery: (previousQuery: Node | null) => ({ previousQuery }),
        setSuggestedQuery: (suggestedQuery: Node | null) => ({ suggestedQuery }),
        reloadSavedInsights: true,
        duplicateInsight: (insight: QueryBasedInsightModel, redirectToInsight = false) => ({
            insight,
            redirectToInsight,
        }),
        deleteInsight: (dashboardId: number | null) => ({ dashboardId }),
        confirmDeleteInsight: (dashboardId: number | null) => ({ dashboardId }),
        setInsightFeedback: (feedback: 'liked' | 'disliked') => ({ feedback }),
    }),
    loaders(({ actions, values, props }) => ({
        insight: [
            props.cachedInsight ?? createEmptyInsight(props.dashboardItemId || 'new'),
            {
                loadInsight: async (
                    { shortId, filtersOverride, variablesOverride, tileFiltersOverride },
                    breakpoint
                ) => {
                    await breakpoint(100)
                    try {
                        const insight = await insightsApi.getByShortId(
                            shortId,
                            undefined,
                            'async',
                            filtersOverride,
                            variablesOverride,
                            tileFiltersOverride
                        )

                        if (!insight) {
                            throw new Error(`Insight with shortId ${shortId} not found`)
                        }

                        const convertedQuery = convertDataTableNodeToDataVisualizationNode(insight.query ?? null)

                        return {
                            ...insight,
                            query: convertedQuery,
                        }
                    } catch (error: any) {
                        if (error.status === 403 && error.code === 'permission_denied') {
                            actions.setAccessDeniedToInsight()
                        }
                        throw error
                    }
                },
                updateInsight: async ({ insightUpdate, callback }, breakpoint) => {
                    if (!Object.entries(insightUpdate).length) {
                        return values.insight
                    }
                    // Resolve the numeric ID. When updateInsight races with loadInsight
                    // (e.g. adding to a dashboard right after save), values.insight.id
                    // may still be undefined. Fall back to resolving via short_id.
                    const insightId =
                        values.insight.id ||
                        (values.insight.short_id ? await getInsightId(values.insight.short_id) : undefined)
                    if (!insightId) {
                        throw new Error('Cannot update insight: unable to resolve insight id')
                    }
                    const response = await insightsApi.update(insightId, insightUpdate)
                    // Call the callback before breakpoint so it fires even if a newer loader
                    // action was dispatched while the API call was in flight. The API call
                    // succeeded, so the callback (e.g. navigation after adding to dashboard)
                    // should run regardless.
                    callback?.()
                    breakpoint()
                    const updatedInsight: QueryBasedInsightModel = {
                        ...response,
                        result: response.result || values.insight.result,
                    }

                    const removedDashboards = (values.insight.dashboards || []).filter(
                        (d) => !updatedInsight.dashboards?.includes(d)
                    )
                    dashboardsModel.actions.updateDashboardInsight(updatedInsight, removedDashboards)
                    values.insight.short_id && refreshTreeItem('insight', String(values.insight.short_id))
                    return updatedInsight
                },
                setInsightMetadata: async ({ metadataUpdate }, breakpoint) => {
                    // new insight
                    if (values.insight.short_id == null) {
                        return { ...values.insight, ...metadataUpdate }
                    }

                    const beforeUpdates: Record<string, any> = {}
                    for (const key of Object.keys(metadataUpdate)) {
                        beforeUpdates[key] = values.savedInsight[key as keyof QueryBasedInsightModel]
                    }

                    const response = await insightsApi.update(values.insight.id as number, metadataUpdate)
                    await breakpoint(300)

                    actions.reloadSavedInsights()
                    dashboardsModel.findMounted()?.actions.updateDashboardInsight(response)
                    actions.loadTags()

                    refreshTreeItem('insight', values.insight.short_id)
                    lemonToast.success(`Updated insight`, {
                        button: {
                            label: 'Undo',
                            dataAttr: 'edit-insight-undo',
                            action: async () => {
                                const response = await insightsApi.update(values.insight.id as number, beforeUpdates)
                                actions.reloadSavedInsights()
                                dashboardsModel.findMounted()?.actions.updateDashboardInsight(response)
                                actions.setInsight(response, { overrideQuery: false, fromPersistentApi: true })
                                lemonToast.success('Insight change reverted')
                            },
                        },
                    })

                    return response
                },
            },
        ],
    })),
    reducers(({ props }) => ({
        highlightedSeries: [
            null as IndexedTrendResult | null,
            {
                highlightSeries: (_, { series }) => series,
            },
        ],
        insight: {
            loadInsight: (state, { shortId }) =>
                shortId === state.short_id
                    ? state
                    : {
                          // blank slate if switched to a new insight
                          short_id: shortId,
                          tags: [],
                          result: null,
                          query: null,
                      },
            setInsight: (state, { insight }) => {
                // Preserve the user-edited name when loading new data for the same insight,
                // but not when switching to a brand new insight
                const isSameInsight = insight.short_id && insight.short_id === state.short_id
                if (!insight.name && state.name && isSameInsight) {
                    return {
                        ...insight,
                        name: state.name,
                    }
                }
                return { ...insight }
            },
            // Note: setInsightMetadata state updates are handled by the loader
            setInsightMetadataLocal: (state, { metadataUpdate }) => ({ ...state, ...metadataUpdate }),
            [dashboardsModel.actionTypes.updateDashboardInsight]: (
                state: Partial<QueryBasedInsightModel>,
                {
                    insight,
                    extraDashboardIds,
                    sourceDashboardId,
                }: { insight: QueryBasedInsightModel; extraDashboardIds?: number[]; sourceDashboardId?: number }
            ) => {
                // Dashboard refresh responses merge that dashboard's filters into `query`; only the embedded
                // insight for that dashboard (`props.dashboardId`) should apply them. Other dashboards or
                // non-dashboard views should ignore this action when `sourceDashboardId` is set.
                if (sourceDashboardId != null) {
                    if (props.dashboardId !== sourceDashboardId) {
                        return state
                    }
                }
                const targetDashboards = (insight?.dashboards || []).concat(extraDashboardIds || [])
                const updateIsForThisDashboard =
                    insight?.short_id === state.short_id &&
                    props.dashboardId &&
                    targetDashboards.includes(props.dashboardId)
                if (updateIsForThisDashboard) {
                    // Dashboard refreshes return a view-only query with the dashboard filters applied.
                    // Keep that query out of the canonical insight state that the editor persists.
                    if (sourceDashboardId != null) {
                        const { query: _query, result: _result, ...insightWithoutDashboardOverrides } = insight
                        return { ...state, ...insightWithoutDashboardOverrides }
                    }
                    return { ...state, ...insight }
                }
                return state
            },
            [insightsModel.actionTypes.renameInsightSuccess]: (
                state: Partial<QueryBasedInsightModel>,
                { item }: { item: QueryBasedInsightModel }
            ) => {
                if (item.id === state.id) {
                    // Also sync query (display-option saves); preserve result — bare PATCHes return result: null.
                    return {
                        ...state,
                        name: item.name,
                        description: item.description,
                        query: (item.query ?? state.query) as QueryBasedInsightModel['query'],
                    }
                }
                return state
            },
            [insightsModel.actionTypes.insightsAddedToDashboard]: (
                state: Partial<QueryBasedInsightModel>,
                { dashboardId, insightIds }: { dashboardId: number; insightIds: number[] }
            ) => {
                if (state.id != null && insightIds.includes(state.id)) {
                    return { ...state, dashboards: [...(state.dashboards || []), dashboardId] }
                }
                return state
            },
            [dashboardsModel.actionTypes.tileRemovedFromDashboard]: (
                state: Partial<QueryBasedInsightModel>,
                { tile, dashboardId }: { tile?: DashboardTile<QueryBasedInsightModel>; dashboardId?: number }
            ) => {
                if (tile?.insight?.id === state.id) {
                    return { ...state, dashboards: state.dashboards?.filter((d: number) => d !== dashboardId) }
                }
                return state
            },
            [dashboardsModel.actionTypes.deleteDashboardSuccess]: (
                state: Partial<QueryBasedInsightModel>,
                { dashboard }: { dashboard: DashboardType<QueryBasedInsightModel> }
            ) => {
                const { id } = dashboard
                return { ...state, dashboards: state.dashboards?.filter((d: number) => d !== id) }
            },
        },
        accessDeniedToInsight: [false, { setAccessDeniedToInsight: () => true }],
        /** The insight's state as it is in the database. */
        savedInsight: [
            () => props.cachedInsight || ({} as Partial<QueryBasedInsightModel>),
            {
                setInsight: (state, { insight, options: { fromPersistentApi } }) =>
                    fromPersistentApi ? { ...insight, query: insight.query || null } : state,
                loadInsightSuccess: (_, { insight }) => ({
                    ...insight,
                    query: insight.query || null,
                }),
                updateInsightSuccess: (_, { insight }) => ({
                    ...insight,
                    query: insight.query || null,
                }),
                setInsightMetadataSuccess: (state, { insight }) => ({
                    ...state,
                    name: insight.name,
                    description: insight.description,
                    tags: insight.tags,
                    favorited: insight.favorited,
                }),
                [insightsModel.actionTypes.renameInsightSuccess]: (
                    state: Partial<QueryBasedInsightModel>,
                    { item }: { item: QueryBasedInsightModel }
                ) =>
                    item.id === state.id
                        ? { ...state, name: item.name, description: item.description, query: item.query ?? state.query }
                        : state,
            },
        ],
        insightLoading: [
            false,
            {
                loadInsight: () => true,
                loadInsightSuccess: () => false,
                loadInsightFailure: () => false,
            },
        ],
        insightSaving: [
            false,
            {
                saveInsight: () => true,
                saveInsightSuccess: () => false,
                saveInsightFailure: () => false,
            },
        ],
        isSavingTags: [
            false,
            {
                setInsightMetadata: (_, { metadataUpdate }) => !!metadataUpdate.tags,
                setInsightMetadataSuccess: () => false,
                setInsightMetadataFailure: () => false,
            },
        ],
        previousQuery: [
            null as Node | null,
            {
                setPreviousQuery: (_, { previousQuery }) => previousQuery,
                saveInsight: () => null,
            },
        ],
        suggestedQuery: [
            null as Node | null,
            {
                setSuggestedQuery: (_, { suggestedQuery }) => suggestedQuery,
                saveInsight: () => null,
            },
        ],
        insightFeedback: [
            null as 'liked' | 'disliked' | null,
            {
                persist: true,
                prefix: `${window.POSTHOG_APP_CONTEXT?.current_team?.id}_`,
            },
            {
                setInsightFeedback: (_, { feedback }) => feedback,
            },
        ],
    })),
    selectors({
        insightProps: [() => [(_, props) => props], (props): InsightLogicProps => props],
        query: [
            (s) => [(state) => insightDataLogic.findMounted(s.insightProps(state))?.values.query || null],
            (node: Node<Record<string, any>> | null): Node | null => node,
        ],
        isInDashboardContext: [() => [(_, props) => props], ({ dashboardId }) => !!dashboardId],
        hasDashboardItemId: [
            () => [(_, props) => props],
            (props: InsightLogicProps) =>
                !!props.dashboardItemId && props.dashboardItemId !== 'new' && !props.dashboardItemId.startsWith('new-'),
        ],
        isInExperimentContext: [
            () => [router.selectors.location],
            ({ pathname }) => /^.*\/experiments\/\d+$/.test(pathname),
        ],
        isInViewMode: [() => [router.selectors.location], ({ pathname }) => /\/insights\/[a-zA-Z0-9]+$/.test(pathname)],
        derivedName: [
            (s) => [s.query, s.aggregationLabel, s.cohortsById, s.mathDefinitions],
            (
                query: Node | null,
                aggregationLabel: (
                    groupTypeIndex: number | null | undefined,
                    deferToUserWording?: boolean
                ) => import('~/models/groupsModel').Noun,
                cohortsById: Partial<Record<number | string, import('~/types').CohortType>>,
                mathDefinitions: Partial<Record<string, import('scenes/trends/mathsLogic').MathDefinition>>
            ) =>
                summarizeInsight(query, {
                    aggregationLabel,
                    cohortsById,
                    mathDefinitions,
                }).slice(0, 400),
        ],
        insightName: [
            (s) => [s.insight, s.derivedName],
            (insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>, derivedName: string) =>
                insight.name ?? derivedName,
        ],
        insightId: [
            (s) => [s.insight],
            (insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>) => insight?.id || null,
        ],
        canEditInsight: [
            (s) => [s.insight],
            (insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>) =>
                insight.user_access_level
                    ? accessLevelSatisfied(
                          AccessControlResourceType.Insight,
                          insight.user_access_level,
                          AccessControlLevel.Editor
                      )
                    : true,
        ],
        insightChanged: [
            (s) => [s.insight, s.savedInsight],
            (
                insight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>,
                savedInsight: Partial<QueryBasedInsightModel<Node<Record<string, any>>>>
            ): boolean => {
                return (
                    (insight.name || '') !== (savedInsight.name || '') ||
                    (insight.description || '') !== (savedInsight.description || '') ||
                    !objectsEqual(insight.tags || [], savedInsight.tags || [])
                )
            },
        ],
        showPersonsModal: [
            (s) => [s.query, s.insightProps, s.featureFlags],
            (query: Record<string, any>, insightProps: InsightLogicProps): boolean => {
                const theQuery = query || insightProps?.query
                return !theQuery || !theQuery.hidePersonsModal
            },
        ],
        supportsCreatingExperiment: [
            (s) => [s.insight, s.activeSceneId],
            (insight: QueryBasedInsightModel, activeSceneId: Scene) =>
                insight?.query &&
                isValidQueryForExperiment(insight.query) &&
                ![
                    Scene.Experiment,
                    Scene.Experiments,
                    Scene.ExperimentsSharedMetric,
                    Scene.ExperimentsSharedMetrics,
                ].includes(activeSceneId),
        ],
        isUsingPathsV1: [
            (s) => [s.featureFlags],
            (featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet) =>
                !featureFlags[FEATURE_FLAGS.PRODUCT_ANALYTICS_PATHS_V2],
        ],
        isUsingPathsV2: [
            (s) => [s.featureFlags],
            (featureFlags: import('lib/logic/featureFlagLogic').FeatureFlagsSet) =>
                featureFlags[FEATURE_FLAGS.PRODUCT_ANALYTICS_PATHS_V2],
        ],
        hasOverrides: [
            () => [
                (_, props) => props.filtersOverride,
                (_, props) => props.variablesOverride,
                (_, props) => props.tileFiltersOverride,
            ],
            (
                filtersOverride: DashboardFilter | null,
                variablesOverride: Record<string, HogQLVariable> | null,
                tileFiltersOverride: TileFilters | null
            ) => {
                return (
                    !isDashboardFilterEmpty(filtersOverride) ||
                    (isObject(variablesOverride) && !isEmptyObject(variablesOverride)) ||
                    !isDashboardFilterEmpty(tileFiltersOverride)
                )
            },
        ],
        editingDisabledReason: [
            (s) => [s.hasOverrides],
            (hasOverrides: boolean) => (hasOverrides ? 'Discard overrides to edit the insight.' : null),
        ],
    }),
    listeners(({ actions, values, props }) => ({
        saveInsight: async ({ redirectToViewMode, folder }) => {
            const insightNumericId =
                values.insight.id || (values.insight.short_id ? await getInsightId(values.insight.short_id) : undefined)
            const { name, description, favorited, deleted, dashboards, tags } = values.insight

            let savedInsight: QueryBasedInsightModel

            try {
                // We don't want to send ALL the insight properties back to the API, so only grabbing fields that might have changed
                const insightRequest: Partial<QueryBasedInsightModel> = {
                    name,
                    derived_name: values.derivedName,
                    description,
                    favorited,
                    query: values.query,
                    deleted,
                    saved: true,
                    dashboards,
                    tags,
                }

                savedInsight = insightNumericId
                    ? await insightsApi.update(insightNumericId, insightRequest)
                    : await insightsApi.create({
                          ...insightRequest,
                          _create_in_folder: folder ?? getLastNewFolder(),
                      })
                actions.reloadSavedInsights() // Load insights afresh

                const alertsLogic = insightAlertsLogic.findMounted({
                    insightLogicProps: props,
                    insightId: insightNumericId,
                })

                if (alertsLogic != null) {
                    if (alertsLogic.values.alerts.length > 0) {
                        alertsLogic.actions.loadAlerts()
                    }
                }
                // remove draft query from local storage
                localStorage.removeItem(`draft-query-${values.currentTeamId}`)
                actions.saveInsightSuccess()
            } catch (e) {
                actions.saveInsightFailure()
                if (e instanceof ApiError) {
                    lemonToast.error(e.detail ?? 'Could not save insight')
                } else {
                    lemonToast.error('Could not save insight')
                }
                throw e
            }

            // the backend can't return the result for a query based insight,
            // and so we shouldn't copy the result from `values.insight` as it might be stale
            const result = savedInsight.result || (values.query ? values.insight.result : null)
            actions.setInsight({ ...savedInsight, result: result }, { fromPersistentApi: true, overrideQuery: true })
            eventUsageLogic.actions.reportInsightSaved(
                savedInsight,
                values.query,
                insightNumericId === undefined,
                'save'
            )
            lemonToast.success(`Insight saved${dashboards?.length === 1 ? ' & added to dashboard' : ''}`, {
                button: {
                    label: 'View Insights list',
                    action: () => router.actions.push(urls.savedInsights()),
                },
            })
            const insightName = savedInsight.name || savedInsight.derived_name
            tryShowMCPHint('insights.create', {
                derivedPrompt: insightName ? `Build an insight called ${insightName}` : undefined,
            })

            dashboardsModel.findMounted()?.actions.updateDashboardInsight(savedInsight)

            // Properly track activation events
            const tasksToComplete: SetupTaskId[] = [SetupTaskId.CreateFirstInsight]
            const query = isNodeWithSource(savedInsight.query) ? savedInsight.query.source : savedInsight.query

            if (isTrendsQuery(query)) {
                tasksToComplete.push(SetupTaskId.ExploreTrendsInsight)
            } else if (isFunnelsQuery(query)) {
                tasksToComplete.push(SetupTaskId.CreateFunnel)
            } else if (isRetentionQuery(query)) {
                tasksToComplete.push(SetupTaskId.ExploreRetentionInsight)
            } else if (isPathsQuery(query)) {
                tasksToComplete.push(SetupTaskId.ExplorePathsInsight)
            } else if (isStickinessQuery(query)) {
                tasksToComplete.push(SetupTaskId.ExploreStickinessInsight)
            } else if (isLifecycleQuery(query)) {
                tasksToComplete.push(SetupTaskId.ExploreLifecycleInsight)
            }

            globalSetupLogic.findMounted()?.actions.markTaskAsCompleted(tasksToComplete)

            // reload dashboards with updated insight
            // since filters on dashboard might be different from filters on insight
            // we need to trigger dashboard reload to pick up results for updated insight
            savedInsight.dashboard_tiles?.forEach(({ dashboard_id }) =>
                dashboardLogic
                    .findMounted({ id: dashboard_id })
                    ?.actions.loadDashboard({ action: DashboardLoadAction.Update })
            )

            if (redirectToViewMode) {
                if (!insightNumericId && dashboards?.length === 1) {
                    // redirect new insights added to dashboard to the dashboard
                    router.actions.push(urls.dashboard(dashboards[0], savedInsight.short_id))
                } else if (insightNumericId) {
                    if (isInsightSceneInstance(props)) {
                        insightSceneLogic
                            .findMounted()
                            ?.actions.setInsightMode(ItemMode.View, InsightEventSource.InsightHeader)
                    }
                } else {
                    router.actions.push(urls.insightView(savedInsight.short_id))
                }
            } else if (!insightNumericId) {
                // If we've just saved a new insight without redirecting to view mode, we need to redirect to edit mode
                // so that we aren't stuck on /insights/new
                router.actions.push(urls.insightEdit(savedInsight.short_id))
            }
        },
        saveAs: async ({ redirectToViewMode, persist, folder }) => {
            LemonDialog.openForm({
                title: 'Save as new insight',
                initialValues: {
                    name:
                        values.insight.name || values.insight.derived_name
                            ? `${values.insight.name || values.insight.derived_name} (copy)`
                            : '',
                },
                content: (
                    <LemonField name="name">
                        <LemonInput data-attr="insight-name" placeholder="Please enter the new name" autoFocus />
                    </LemonField>
                ),
                errors: {
                    name: (name) => (!name ? 'You must enter a name' : undefined),
                },
                onSubmit: async ({ name }) => actions.saveAsConfirmation(name, redirectToViewMode, persist, folder),
            })
        },
        saveAsConfirmation: async ({ name, redirectToViewMode, persist, folder }) => {
            const insight = await insightsApi.create({
                name,
                query: values.query,
                saved: true,
                _create_in_folder: folder ?? getLastNewFolder(),
            })

            if (router.values.location.pathname.includes(urls.sqlEditor())) {
                lemonToast.info(`You're now viewing ${values.insight.name || values.insight.derived_name || name}`)
            } else {
                lemonToast.info(
                    `You're now working on a copy of ${values.insight.name || values.insight.derived_name || name}`
                )
            }

            if (persist) {
                eventUsageLogic.actions.reportInsightSaved(insight, values.query, true, 'save_as')
            }
            actions.reloadSavedInsights() // Load insights afresh

            if (redirectToViewMode) {
                router.actions.push(urls.insightView(insight.short_id))
            } else {
                router.actions.push(urls.insightEdit(insight.short_id))
            }
        },
        onRejectSuggestedInsight: () => {
            if (values.previousQuery) {
                const insightDataLogicInstance = insightDataLogic.findMounted(values.insightProps)
                if (insightDataLogicInstance) {
                    insightDataLogicInstance.actions.setQuery(values.previousQuery)
                }
                actions.setPreviousQuery(null)
            } else {
            }
        },
        handleInsightSuggested: ({ suggestedInsight }) => {
            if (suggestedInsight) {
                const insightDataLogicInstance = insightDataLogic.findMounted(values.insightProps)
                if (insightDataLogicInstance) {
                    const currentQuery = insightDataLogicInstance.values.query
                    actions.setPreviousQuery(currentQuery)
                    actions.setSuggestedQuery(suggestedInsight)
                }
            }
        },
        onReapplySuggestedInsight: () => {
            // Reapply the Max AI suggestion
            if (values.suggestedQuery) {
                const insightDataLogicInstance = insightDataLogic.findMounted(values.insightProps)
                if (insightDataLogicInstance) {
                    const currentQuery = insightDataLogicInstance.values.query
                    actions.setPreviousQuery(currentQuery)
                    insightDataLogicInstance.actions.setQuery(values.suggestedQuery)
                }
            } else {
            }
        },
        reloadSavedInsights: () => {
            for (const logic of savedInsightsLogic.findAllMounted()) {
                logic.actions.loadInsights()
            }
        },
        duplicateInsight: async ({ insight, redirectToInsight }) => {
            let insightToDuplicate = insight
            if (insight.short_id) {
                try {
                    const cleanInsight = await insightsApi.getByShortId(insight.short_id)
                    if (cleanInsight) {
                        insightToDuplicate = cleanInsight
                    }
                } catch {
                    // Fall through to duplicate the original insight
                }
            }
            const newInsight = await insightsApi.duplicate(insightToDuplicate)
            for (const logic of savedInsightsLogic.findAllMounted()) {
                logic.actions.addInsight(newInsight)
            }
            redirectToInsight && router.actions.push(urls.insightEdit(newInsight.short_id))
        },
        deleteInsight: ({ dashboardId }) => {
            LemonDialog.open({
                title: 'Delete insight?',
                description: 'Are you sure you want to delete this insight? This action can be undone.',
                primaryButton: {
                    children: 'Delete',
                    status: 'danger',
                    onClick: () => actions.confirmDeleteInsight(dashboardId),
                },
                secondaryButton: { children: 'Cancel' },
            })
        },
        confirmDeleteInsight: async ({ dashboardId }) => {
            const { insight, currentTeamId } = values
            await deleteInsightWithUndo({
                object: insight as QueryBasedInsightModel,
                endpoint: `projects/${currentTeamId}/insights`,
                callback: (undo: boolean) => {
                    if (undo && dashboardId) {
                        dashboardsModel
                            .findMounted()
                            ?.actions.updateDashboardInsight(
                                { ...(insight as QueryBasedInsightModel), deleted: false },
                                [dashboardId]
                            )
                    }
                    actions.reloadSavedInsights()
                },
            })
            if (dashboardId) {
                router.actions.push(urls.dashboard(dashboardId))
                dashboardsModel.actions.updateDashboardInsight(
                    { ...(insight as QueryBasedInsightModel), deleted: true, dashboards: [] },
                    [dashboardId]
                )
            } else {
                router.actions.push(urls.savedInsights())
            }
        },
        setInsightFeedback: ({ feedback }) => {
            const eventName = `customer-analytics-insight-${feedback}`
            posthog.capture(eventName, {
                insight_id: values.insight.short_id,
                insight_name: values.insight.name,
                dashboard_id: values.insightProps.dashboardId,
            })
            lemonToast.success(`Insight ${feedback}`)
        },
    })),
    events(({ props, actions }) => ({
        afterMount: () => {
            if (!props.dashboardItemId || props.dashboardItemId === 'new' || props.dashboardItemId.startsWith('new-')) {
                return
            }

            if (!props.doNotLoad && !props.cachedInsight) {
                actions.loadInsight(
                    props.dashboardItemId as InsightShortId,
                    props.filtersOverride,
                    props.variablesOverride,
                    props.tileFiltersOverride
                )
            }
        },
    })),
])
